// Shared meeting bucketing: "is this meeting done, coming up, or canceled?"
//
// Used by the Meetings tab (to group the list) and by the follow-up surfaces
// (to show the next meeting alongside the next follow-up). Both need the exact
// same answer, so the rule lives here once.
//
// A meeting counts as done when the org marked it completed OR its end time has
// passed — the status field only flips when something explicitly closes the
// meeting, so time-based fallback is what keeps yesterday's untouched meeting
// out of "Upcoming".

export const MEETING_STATE = {
  UPCOMING: "upcoming",
  DONE: "done",
  CANCELED: "canceled",
};

export function meetingState(meeting, now = Date.now()) {
  if (!meeting) return MEETING_STATE.DONE;
  if (meeting.status === "canceled") return MEETING_STATE.CANCELED;
  if (meeting.status === "completed") return MEETING_STATE.DONE;
  // Fall back to the end time, then the start time, so a meeting missing an end
  // still ages out instead of sitting in Upcoming forever.
  const endsAt = meeting.scheduledEnd || meeting.scheduledStart;
  if (!endsAt) return MEETING_STATE.DONE;
  return new Date(endsAt).getTime() > now ? MEETING_STATE.UPCOMING : MEETING_STATE.DONE;
}

function startTime(meeting) {
  const raw = meeting?.scheduledStart;
  const ms = raw ? new Date(raw).getTime() : NaN;
  return Number.isNaN(ms) ? 0 : ms;
}

// Returns [{ label, state, items }] with empty groups dropped, ready to render.
export function splitMeetingsByState(meetings = [], now = Date.now()) {
  const buckets = { upcoming: [], done: [], canceled: [] };
  for (const m of meetings) buckets[meetingState(m, now)].push(m);

  buckets.upcoming.sort((a, b) => startTime(a) - startTime(b)); // soonest first
  buckets.done.sort((a, b) => startTime(b) - startTime(a));     // newest first
  buckets.canceled.sort((a, b) => startTime(b) - startTime(a));

  return [
    { label: "Upcoming", state: MEETING_STATE.UPCOMING, items: buckets.upcoming },
    { label: "Done", state: MEETING_STATE.DONE, items: buckets.done },
    { label: "Canceled", state: MEETING_STATE.CANCELED, items: buckets.canceled },
  ].filter((g) => g.items.length > 0);
}

// The single meeting to surface next to a lead's follow-up date: the soonest
// one that hasn't happened yet. Null when there's nothing scheduled ahead.
export function nextUpcomingMeeting(meetings = [], now = Date.now()) {
  const upcoming = meetings.filter((m) => meetingState(m, now) === MEETING_STATE.UPCOMING);
  if (!upcoming.length) return null;
  return upcoming.reduce((soonest, m) => (startTime(m) < startTime(soonest) ? m : soonest));
}
